/**
 * ConnectionHealthBar — Phase 1
 *
 * Fixed bottom bar showing real-time connection status, participant count,
 * and last-sync hint. When disconnected it says the room is reconnecting by
 * itself (useRoomReconnect, S-7) — no reload needed.
 * This is the trust layer — participants know their vote counted.
 */

import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useSessionMessagesStore } from '../../../stores/prioritization/sessionMessagesStore';
import { C } from '../theme/participantTokens';

interface ConnectionHealthBarProps {
  sessionId: string;
}

export function ConnectionHealthBar({ sessionId }: ConnectionHealthBarProps) {
  const channelStatus = useSessionStore(s => s._sessionChannelStatus?.[sessionId] ?? 'disconnected');
  const presenceConnected = usePresenceStore(s => s.isConnected);
  const presentCount = usePresenceStore(s => s.presentUsers.length);
  const chatConnected = useSessionMessagesStore(s => s.isRealtimeConnected);

  const allOk = channelStatus === 'connected' && presenceConnected;
  const partial = !allOk && (presenceConnected || chatConnected);

  const state: 'live' | 'partial' | 'offline' = allOk ? 'live' : partial ? 'partial' : 'offline';

  const config = {
    live: {
      dot: { background: '#34d399', animation: 'pulse 2s infinite' },
      label: 'Live',
      sublabel: `${presentCount} in room`,
      bg: 'transparent',
      border: 'transparent',
      textColor: C.textDim,
      prompt: null,
    },
    partial: {
      dot: { background: 'var(--mustard)' },
      label: 'Reconnecting…',
      sublabel: 'Some channels degraded',
      bg: 'rgba(212,160,23,0.06)',
      border: 'rgba(212,160,23,0.18)',
      textColor: C.mustard,
      prompt: null,
    },
    offline: {
      dot: { background: '#f87171' },
      label: 'Offline',
      sublabel: 'Changes may not sync',
      bg: 'rgba(248,113,113,0.07)',
      border: 'rgba(248,113,113,0.22)',
      textColor: '#f87171',
      prompt: 'Your connection dropped. Reconnecting by itself — your answers are kept.',
    },
  }[state];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 87,
        background: state === 'live' ? 'rgba(18,22,28,0.85)' : config.bg,
        borderTop: `1px solid ${state === 'live' ? C.rule : config.border}`,
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 16px',
        height: 32,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          ...config.dot,
        }}
      />
      <span
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.58rem',
          letterSpacing: '0.1em',
          color: config.textColor,
          textTransform: 'uppercase',
        }}
      >
        {config.label}
      </span>
      <span
        style={{
          fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          fontSize: '0.68rem',
          color: C.textDim,
        }}
      >
        ·
      </span>
      <span
        style={{
          fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          fontSize: '0.68rem',
          color: C.textDim,
        }}
      >
        {config.sublabel}
      </span>
      {config.prompt && (
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
            fontSize: '0.68rem',
            color: '#f87171',
          }}
        >
          {config.prompt}
        </span>
      )}
    </div>
  );
}
